import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Sparkles, BadgeCheck, Globe2, GraduationCap, TrendingUp } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 * i, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-24 md:pt-32 pb-16 md:pb-24 overflow-hidden bg-white"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 grid-pattern pointer-events-none" aria-hidden />
      <div className="absolute -top-24 -left-20 h-72 w-72 rounded-full bg-[#0A3161]/10 blur-3xl animate-blob" aria-hidden />
      <div className="absolute -top-10 -right-16 h-72 w-72 rounded-full bg-[#B31942]/10 blur-3xl animate-blob" style={{ animationDelay: "3s" }} aria-hidden />
      <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-[#0A3161]/6 blur-3xl animate-blob" style={{ animationDelay: "6s" }} aria-hidden />

      {/* Floating decorative shapes */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        className="hidden md:block absolute top-32 left-8 h-16 w-16 rounded-3xl bg-white border border-[#0A3161]/10 soft-shadow rotate-6 animate-floaty-slow"
      >
        <div className="h-full w-full flex items-center justify-center">
          <GraduationCap className="h-7 w-7 text-[#0A3161]" strokeWidth={2.25} />
        </div>
      </motion.div>
      
      <div className="container-x relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* Text */}
        <div className="lg:col-span-7">
          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-[#0A3161]/15 bg-[#0A3161]/5 px-3.5 py-1.5 text-[11px] md:text-[12px] font-semibold text-[#0A3161] tracking-wide"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.25} />
            LEARN · UPSKILL · GROW · SUCCEED
          </motion.div>

          <motion.h1
            // variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="mt-5 md:mt-6 font-display font-extrabold tracking-tight text-[36px] leading-[1.08] sm:text-5xl md:text-6xl lg:text-[64px] text-[#0A3161]"
            data-testid="hero-heading"
          >
            Empowering Future Leaders <br className="hidden md:block" />
            Through <span className="text-gradient-brand">Quality Education</span>
          </motion.h1>

          <motion.p
            variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-5 md:mt-6 max-w-2xl text-[15px] md:text-lg text-slate-600 leading-relaxed"
            data-testid="hero-subheading"
          >
            Master industry-ready skills through expert-led training, international
            education guidance, certifications, internships and placement assistance —
            trusted by 10,000+ students and 500+ hiring partners.
          </motion.p>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={3}
            className="mt-7 md:mt-8 flex flex-wrap items-center gap-3"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-explore-courses"
              onClick={() => scrollTo("#courses")}
              className="group inline-flex items-center gap-2 rounded-full bg-[#B31942] px-5 md:px-6 py-3 md:py-3.5 text-white text-sm font-semibold shadow-[0_14px_40px_rgba(229,57,53,0.32)] hover:bg-[#940835] transition-colors"
            >
              Explore Courses
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2.5} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-study-abroad"
              onClick={() => scrollTo("#abroad")}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-[#B31942]/25 px-5 md:px-6 py-3 md:py-3.5 text-[#B31942] text-sm font-semibold hover:border-[#B31942]/50 hover:bg-[#FEF2F2] transition-colors"
            >
              <Globe2 className="h-4 w-4" strokeWidth={2.25} /> Study Abroad
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-consultation"
              onClick={() => scrollTo("#contact")}
              className="inline-flex items-center gap-2 rounded-full bg-[#0A3161] px-5 md:px-6 py-3 md:py-3.5 text-white text-sm font-semibold hover:bg-[#0E3F84] shadow-[0_14px_40px_rgba(10,61,145,0.24)] transition-colors"
            >
              Book Free Consultation
            </motion.button>
          </motion.div>

          <motion.div
            variants={fadeUp} initial="hidden" animate="show" custom={5}
            className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600"
          >
            <span className="inline-flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#0A3161]" strokeWidth={2.25} /> Industry-recognized certification</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#0A3161]" strokeWidth={2.25} /> 100% placement support</span>
            <span className="inline-flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-[#0A3161]" strokeWidth={2.25} /> EMI · Scholarships available</span>
          </motion.div>
        </div>

        {/* Image */}
        <div className="lg:col-span-5">
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-[28px] overflow-hidden border border-black/5 premium-shadow bg-white"
            >
              <img
                src="https://images.unsplash.com/photo-1758270705172-07b53627dfcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHx1bml2ZXJzaXR5JTIwc3R1ZGVudHMlMjBkaXZlcnNlJTIwbW9kZXJuJTIwY2FtcHVzfGVufDB8fHx8MTc4Mjc1NzI2OHww&ixlib=rb-4.1.0&q=85"
                alt="Diverse students collaborating on a laptop"
                className="w-full h-[340px] sm:h-[420px] md:h-[520px] object-cover"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3161]/25 via-transparent to-transparent" />
              {/* <button
                data-testid="hero-play"
                className="absolute inset-0 flex items-center justify-center group"
                aria-label="Watch intro"
              >
                <span className="inline-flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-4 py-2.5 text-[#0A3161] font-semibold text-sm shadow-lg group-hover:bg-white transition-colors"> 
                   <PlayCircle className="h-5 w-5 text-[#B31942]" strokeWidth={2.25} /> Watch 2-min intro
                </span>
              </button> */}
            </motion.div>

            {/* Floating badge - placement */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="absolute -left-2 sm:-left-4 md:-left-10 top-6 md:top-8 rounded-2xl glass p-3 pr-4 flex items-center gap-3 soft-shadow-lg animate-floaty"
              data-testid="hero-badge-placements"
            >
              <div className="h-10 w-10 rounded-xl bg-[#0A3161] text-white flex items-center justify-center font-bold text-sm">95%</div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Placement</div>
                <div className="text-sm font-semibold text-slate-900">Support Rate</div>
              </div>
            </motion.div>

            {/* Floating badge - partners */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="absolute -right-2 sm:-right-3 md:-right-6 bottom-6 md:bottom-8 rounded-2xl bg-white border border-black/5 p-3 flex items-center gap-3 soft-shadow-lg"
              data-testid="hero-badge-hired"
            >
              <div className="h-10 w-10 rounded-xl bg-[#B31942] text-white flex items-center justify-center font-bold text-sm">500+</div>
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Hiring</div>
                <div className="text-sm font-semibold text-slate-900">Partner Companies</div>
              </div>
            </motion.div>

            {/* Floating badge - trend */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="hidden md:flex absolute -bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white border border-black/5 px-4 py-2 items-center gap-2 soft-shadow-lg"
              data-testid="hero-badge-rating"
            >
              <TrendingUp className="h-4 w-4 text-[#B31942]" strokeWidth={2.5} />
              <span className="text-xs font-semibold text-slate-800">4.9/5 · 3,200+ reviews</span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
